import {
  PDCycle,
  PDActivity,
  PDStats,
  PDActivityCategory,
  CATEGORY_INFO,
  PD_REQUIREMENTS,
} from './types';

/**
 * Calculate the CRPO PD cycle dates based on registration date
 *
 * Cycle rules:
 * - Established registrants (pre-2023): Odd registration years cycle with odd years (2024-2025, 2026-2027)
 *   Even registration years cycle with even years (2025-2026, 2027-2028)
 * - New registrants (2023+): First cycle starts from their registration date
 *   - 2023: Registration date – Dec 31, 2025
 *   - 2024: Registration date – Dec 31, 2026
 *   - 2025: Registration date – Dec 31, 2027
 */
export function getCRPOCycle(registrationDate: Date): PDCycle {
  const regYear = registrationDate.getFullYear();
  const now = new Date();
  const currentYear = now.getFullYear();

  // New registrants (2023+): first cycle starts from registration date
  if (regYear >= 2023) {
    // Determine end year based on registration year
    let endYear: number;
    if (regYear === 2023) endYear = 2025;
    else if (regYear === 2024) endYear = 2026;
    else if (regYear === 2025) endYear = 2027;
    else {
      // Future registrants follow the pattern
      // Odd year registrants end in odd years (regYear + 2 if odd)
      // Even year registrants end in even years (regYear + 2 if even)
      endYear = regYear + 2;
    }

    const cycleEnd = new Date(`${endYear}-12-31T23:59:59`);

    // Check if still in first cycle
    if (now <= cycleEnd) {
      return {
        start: registrationDate,
        end: cycleEnd,
        isFirstCycle: true,
      };
    }
    // After first cycle, fall through to established pattern
  }

  // Established registrants: odd/even year pattern
  const isOddRegistration = regYear % 2 === 1;

  // Odd registration years: cycles are 2024-2025, 2026-2027, etc. (start in even years)
  // Even registration years: cycles are 2025-2026, 2027-2028, etc. (start in odd years)
  let cycleStartYear: number;

  if (isOddRegistration) {
    // Odd registration → cycles start in even years (2024, 2026, 2028...)
    cycleStartYear = currentYear % 2 === 0 ? currentYear : currentYear - 1;
  } else {
    // Even registration → cycles start in odd years (2025, 2027, 2029...)
    cycleStartYear = currentYear % 2 === 1 ? currentYear : currentYear - 1;
  }

  return {
    start: new Date(`${cycleStartYear}-01-01T00:00:00`),
    end: new Date(`${cycleStartYear + 1}-12-31T23:59:59`),
    isFirstCycle: false,
  };
}

/**
 * Calculate days remaining until cycle end
 */
export function getDaysRemaining(cycleEnd: Date): number {
  const now = new Date();
  const diff = cycleEnd.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Format duration from minutes to human-readable string
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins}m`;
  }
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Convert minutes to hours (decimal)
 */
export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100; // Round to 2 decimals
}

/**
 * Format hours for display - shows decimals only when needed
 */
export function formatHours(hours: number): string {
  if (hours === 0 || Number.isInteger(hours)) {
    return hours.toString();
  }
  return hours.toFixed(2);
}

/**
 * Convert hours and minutes to total minutes
 */
export function hoursMinutesToMinutes(hours: number, minutes: number): number {
  return hours * 60 + minutes;
}

/**
 * Calculate statistics from activities
 */
export function calculateStats(
  activities: PDActivity[],
  cycle: PDCycle
): PDStats {
  // Filter activities within the current cycle
  const cycleActivities = activities.filter((a) => {
    const activityDate = new Date(a.activityDate);
    return activityDate >= cycle.start && activityDate <= cycle.end;
  });

  // Calculate totals
  const totalMinutes = cycleActivities.reduce((sum, a) => sum + a.duration, 0);
  const totalHours = minutesToHours(totalMinutes);

  // Calculate by category
  const byCategory = {} as Record<PDActivityCategory, number>;
  for (const activity of cycleActivities) {
    const hours = minutesToHours(activity.duration);
    byCategory[activity.category] = (byCategory[activity.category] || 0) + hours;
  }

  // Calculate by type
  let didacticMinutes = 0;
  let experientialMinutes = 0;
  for (const activity of cycleActivities) {
    if (activity.activityType === 'DIDACTIC') {
      didacticMinutes += activity.duration;
    } else {
      experientialMinutes += activity.duration;
    }
  }

  // Check if requirements are met
  const hasDidactic = cycleActivities.some((a) => a.activityType === 'DIDACTIC');
  const hasExperiential = cycleActivities.some(
    (a) => a.activityType === 'EXPERIENTIAL'
  );

  return {
    cycleStartDate: cycle.start.toISOString(),
    cycleEndDate: cycle.end.toISOString(),
    totalHours,
    targetHours: PD_REQUIREMENTS.totalHours,
    hoursRemaining: Math.max(0, PD_REQUIREMENTS.totalHours - totalHours),
    hasDidactic,
    hasExperiential,
    byCategory,
    byType: {
      didactic: minutesToHours(didacticMinutes),
      experiential: minutesToHours(experientialMinutes),
    },
    activityCount: cycleActivities.length,
    daysRemaining: getDaysRemaining(cycle.end),
    isFirstCycle: cycle.isFirstCycle,
  };
}

/**
 * Format date for display. Callers pass the viewer's stored timezone;
 * defaults to America/Toronto (Alli's canonical zone) rather than the
 * browser to avoid silent day-boundary drift.
 */
export function formatDate(date: Date | string, tz: string = 'America/Toronto'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  });
}

/**
 * Format date range for display in the viewer's timezone.
 */
export function formatDateRange(
  start: Date | string,
  end: Date | string,
  tz: string = 'America/Toronto',
): string {
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;

  const startFormatted = startDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });
  const endFormatted = endDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });

  return `${startFormatted} – ${endFormatted}`;
}

/**
 * Get activity type (didactic/experiential) from category
 */
export function getActivityType(category: PDActivityCategory): 'DIDACTIC' | 'EXPERIENTIAL' {
  return CATEGORY_INFO[category].type;
}

/**
 * Format supervision format for display
 */
function formatSupervisionFormat(format?: string): string {
  if (!format) return '';
  const formatMap: Record<string, string> = {
    INDIVIDUAL: 'Individual',
    DYAD: 'Dyad',
    GROUP: 'Group',
    PEER: 'Peer',
  };
  return formatMap[format] || format;
}

/**
 * Generate CSV content from activities
 */
export function generateCSV(activities: PDActivity[]): string {
  const headers = [
    'Date',
    'Title',
    'Category',
    'Type',
    'Duration (hours)',
    'Provider',
    'Source',
    'Supervision Format',
    'Certificate',
    'Description',
    'Learning Objectives',
    'Key Takeaways',
    'Application Plan',
  ];

  const rows = activities.map((a) => [
    escapeCSV(formatDate(a.activityDate)),
    escapeCSV(a.title),
    escapeCSV(CATEGORY_INFO[a.category].label),
    escapeCSV(a.activityType === 'DIDACTIC' ? 'Didactic' : 'Experiential'),
    escapeCSV(minutesToHours(a.duration).toString()),
    escapeCSV(a.provider || ''),
    escapeCSV(a.isFromSupervision ? 'Supervision' : 'Manual'),
    escapeCSV(formatSupervisionFormat(a.supervisionFormat)),
    escapeCSV(a.certificateUrl || ''),
    escapeCSV(a.description || ''),
    escapeCSV(a.learningObjectives || ''),
    escapeCSV(a.keyTakeaways || ''),
    escapeCSV(a.applicationPlan || ''),
  ]);

  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

/**
 * Escape CSV field and prevent formula injection
 * Fields starting with =, +, -, @, tab, or carriage return could be
 * interpreted as formulas by Excel/Google Sheets
 */
function escapeCSV(field: string): string {
  // Prevent formula injection by prefixing dangerous characters
  if (/^[=+\-@\t\r]/.test(field)) {
    field = "'" + field;
  }
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Download CSV file
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
